/* =========================================================================
   hooks.ts — Shared React hooks for enterprise polish.
   ========================================================================= */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type RefCallback,
} from "react";

/** Detect user prefers-reduced-motion setting. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

/** Simple focus trap for modals/dialogs.
 *  Call `trapRef` on the container element. Trap activates when `active` is true.
 */
export function useFocusTrap(active: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;
    const container = containerRef.current;
    const controller = new AbortController();
    const { signal } = controller;

    // Find all focusable elements
    const focusable = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) return;

    // Focus first element; ignore elements that are not currently focusable.
    try {
      first.focus();
    } catch {
      /* ignore */
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    container.addEventListener("keydown", onKeyDown, { signal });
    return () => controller.abort();
  }, [active]);

  return containerRef;
}

/** Measure the width of a referenced element using ResizeObserver.
 *  Returns `[ref, width]`. Falls back to `initialWidth` before measurement
 *  or when ResizeObserver is unavailable (SSR/tests).
 */
export function useResizeObserver<T extends HTMLElement = HTMLDivElement>(
  initialWidth = 0,
): [RefCallback<T>, number] {
  const [el, setEl] = useState<T | null>(null);
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(initialWidth);

  // Callback ref wrapper keeps the object-ref API stable while tracking the
  // element in state so the ResizeObserver effect re-runs when the ref
  // attaches to a new DOM node (fixes stale ref closure).
  const callbackRef = useCallback((node: T | null) => {
    ref.current = node;
    setEl(node);
  }, []);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined" || !el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);

  return [callbackRef, width];
}

/** useInView — IntersectionObserver-based visibility trigger.
 *  Re-uses the existing implementation from use-in-view.ts for consistency.
 */
export { useInView } from "./use-in-view";

/** useCountUp — Animated number counter.
 *  Re-uses the existing implementation from use-count-up.ts.
 */
export { useCountUp } from "./use-count-up";
